/** Pure helpers for live tests (no DOM, no network) so they can be unit tested. */

export const BLANK_QUESTION = () => ({ q: '', options: ['', '', '', ''], answer: null });

/**
 * Turn what a trainer pasted into questions.
 *
 *   Q: What is a bug?        (or "1. What is a bug?")
 *   A) A defect
 *   B) A feature
 *   Answer: A
 *
 * Questions are separated by a blank line. Returns { questions, errors }; a question with a problem is skipped
 * and described in errors so the trainer can fix just that one.
 */
export function parseQuestions(text) {
  const questions = [];
  const errors = [];
  const blocks = String(text || '').replace(/\r/g, '').split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  blocks.forEach((block, n) => {
    const label = `Question ${n + 1}`;
    let q = '';
    const options = [];
    let answer = null;
    for (const raw of block.split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      const ans = line.match(/^(?:answer|ans|correct)\s*[:=-]\s*\(?([A-Fa-f])\)?\.?\s*$/i);
      const opt = line.match(/^\(?([A-Fa-f])[).:]\s*(.+)$/);
      if (ans) answer = ans[1].toUpperCase().charCodeAt(0) - 65;
      else if (opt && q) options.push(opt[2].trim());
      else if (!q) q = line.replace(/^(?:q(?:uestion)?\s*\d*\s*[:.)-]|\d+\s*[.):-])\s*/i, '').trim();
      else if (options.length) options[options.length - 1] += ` ${line}`;
      else q += ` ${line}`;
    }
    if (!q) errors.push(`${label}: the question text is missing.`);
    else if (options.length < 2) errors.push(`${label}: give at least two options (A, B, ...).`);
    else if (answer == null) errors.push(`${label}: add a line like "Answer: B".`);
    else if (answer >= options.length) errors.push(`${label}: the answer points to an option that does not exist.`);
    else questions.push({ q, options, answer });
  });
  return { questions, errors };
}

/** Check questions from the builder and return clean ones for the API, or an error message. */
export function cleanQuestions(list) {
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    const label = `Question ${i + 1}`;
    const q = String(item.q || '').trim();
    if (!q) return { error: `${label}: type the question.` };
    const kept = [];
    let answer = -1;
    (item.options || []).forEach((o, j) => {
      const text = String(o || '').trim();
      if (!text) return;
      if (item.answer === j) answer = kept.length;
      kept.push(text);
    });
    if (kept.length < 2) return { error: `${label}: fill in at least two options.` };
    if (new Set(kept.map((o) => o.toLowerCase())).size !== kept.length) return { error: `${label}: options must be different from each other.` };
    if (answer < 0) return { error: `${label}: tick the correct answer.` };
    out.push({ q, options: kept, answer });
  }
  if (!out.length) return { error: 'Add at least one question.' };
  return { questions: out };
}

/** m:ss or h:mm:ss for a count of seconds. */
export function clock(total) {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export const REASON = { submitted: 'Submitted', time: 'Time ran out', violations: 'Too many tab switches' };
